import type { Bucket, Priority } from '@/types'

/** Statuses that count as current work for an area (drives area ordering and counts). */
export const WORKING_STATUSES: ReadonlySet<Priority['status']> = new Set(['aspirational', 'active'])

/** A review is overdue once its date has passed; dormant priorities are exempt. */
export function isReviewOverdue(p: Priority, today: string): boolean {
  return !!p.nextReviewDate && p.nextReviewDate < today && p.status !== 'dormant'
}

/** Overdue priorities, most overdue first. */
export function overduePriorities(priorities: Priority[], today: string): Priority[] {
  return priorities
    .filter((p) => isReviewOverdue(p, today))
    .sort((a, b) => a.nextReviewDate!.localeCompare(b.nextReviewDate!))
}

export function countWorkingPrioritiesByBucket(priorities: Priority[]): Map<string, number> {
  const counts = new Map<string, number>()
  for (const p of priorities) {
    if (WORKING_STATUSES.has(p.status) && p.bucketId) {
      counts.set(p.bucketId, (counts.get(p.bucketId) ?? 0) + 1)
    }
  }
  return counts
}

/** User areas auto-ordered: most working priorities first, alphabetical on ties. */
export function orderAreasByActivity(buckets: Bucket[], counts: Map<string, number>): Bucket[] {
  return buckets
    .filter((b) => !b.isSystem)
    .sort((a, b) => {
      const diff = (counts.get(b.id) ?? 0) - (counts.get(a.id) ?? 0)
      return diff !== 0 ? diff : a.name.localeCompare(b.name)
    })
}
